import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import SecondaryButton from '../../buttons/SecondaryButton/SecondaryButton';
import Link from '../../links/Link/Link';

/**
 * A component for consenting to cookie usage
 */
function CookieBanner({ domain, baseUrl, settingsHref, analytics, marketing, personalisation, webchat }) {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [hasAccepted, setHasAccepted] = useState(false);

  const configuration = {
    analytics: {
      cookies: ['_ga', '_gid', '_gat', '_fbp'],
      onAccept: function () {
        if (analytics) {
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', analytics);
        }
      },
      onRevoke: function () {}
    },
    marketing: {
      cookies: [],
      onAccept: function () {
        if (marketing) {
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', marketing);
        }
      },
      onRevoke: function () {}
    },
    personalisation: {
      cookies: [],
      onAccept: function () {
        if (personalisation) {
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', personalisation);
        }
      },
      onRevoke: function () {}
    },
    webchat: {
      cookies: [],
      onAccept: function () {
        if (webchat) {
          (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', webchat);
        }
      },
      onRevoke: function () {}
    }
  };

  useEffect(() => {
    let cookiesPreferences = document.cookie.replace(
      /(?:(?:^|.*;\s*)CWCookiesPreferences\s*\=\s*([^;]*).*$)|^.*$/,
      '$1'
    );
    let cookiesAcknowledged = document.cookie.replace(
      /(?:(?:^|.*;\s*)CWCookiesAcknowledged\s*\=\s*([^;]*).*$)|^.*$/,
      '$1'
    );

    if (!cookiesPreferences) {
      let cookiesPreferencesDefaults = { analytics: 0, marketing: 0, personalisation: 0, webchat: 0 };
      setCookie('CWCookiesPreferences', JSON.stringify(cookiesPreferencesDefaults));
    }

    if (cookiesAcknowledged) {
      runAcceptedCookies();
    } else {
      setShowBanner(true);
    }
  }, []);

  function setCookie(name, value) {
    let domain = new URL(baseUrl).hostname; // Get the hostname from the base URL.
    let expires = new Date(new Date().setFullYear(new Date().getFullYear() + 1)); // Get date 1 year in the future.

    document.cookie = `${name}=${value};domain=${domain};path=/;expires=${expires}`;
  }

  function runAcceptedCookies() {
    let preferences = JSON.parse(
      document.cookie.replace(/(?:(?:^|.*;\s*)CWCookiesPreferences\s*\=\s*([^;]*).*$)|^.*$/, '$1')
    );

    Object.keys(preferences).forEach((category) => {
      if (preferences[category] === 1 && configuration[category]) {
        configuration[category].onAccept();
      }
    });
  }

  function handleAcceptClick() {
    let preferences = { analytics: 1, marketing: 1, personalisation: 1, webchat: 1 };
    setCookie('CWCookiesPreferences', JSON.stringify(preferences));
    setCookie('CWCookiesAcknowledged', 1);

    runAcceptedCookies();

    setHasAccepted(true);
  }

  function handleHideClick() {
    setShowBanner(false);
  }

  return showBanner ? (
    <div
      role="section"
      className="cw-cookies-policy-banner js-cw-cookies-policy-banner"
      style={{ display: 'block' }}
      aria-label={t('styleguide:cookies.ariaLabel')}
    >
      {hasAccepted ? (
        <div className="js-cw-cookies-policy-banner-accepted">
          <div className="cw-cookies-policy-banner__content">
            <SecondaryButton
              className={'button--style-charcoal js-cw-cookies-hide'}
              iconType="cross"
              onPress={handleHideClick}
            >
              {t('styleguide:hide')}
            </SecondaryButton>
            <div
              dangerouslySetInnerHTML={{
                __html:
                  domain == 'ww'
                    ? t('styleguide:ww_cookies_message_accepted')
                    : t('styleguide:cookies_message_accepted')
              }}
            ></div>
          </div>
        </div>
      ) : (
        <div className="js-cw-cookies-policy-banner-prompt" style={{ display: 'block' }}>
          <div className="cw-cookies-policy-banner__content">
            <div dangerouslySetInnerHTML={{ __html: t('styleguide:cookies_message_prompt') }}></div>
          </div>
          <div className="cw-cookies-policy-banner__actions">
            <SecondaryButton
              className={'button--style-brand-secondary js-cw-cookies-accept'}
              onPress={handleAcceptClick}
            >
              {t('styleguide:cookies_accept')}
            </SecondaryButton>
            <Link href={settingsHref} className="button button--secondary button--style-charcoal">
              {t('styleguide:cookies_settings')}
            </Link>
          </div>
        </div>
      )}
    </div>
  ) : null;
}

CookieBanner.propTypes = {
  /**
   * The URL of the base site
   */
  baseUrl: PropTypes.string.isRequired,
  /**
   * The destination of the settings page
   */
  settingsHref: PropTypes.string.isRequired,
  /**
   * The analytics Google Tag
   */
  analytics: PropTypes.string,
  /**
   * The marketing Google Tag
   */
  marketing: PropTypes.string,
  /**
   * The personalisation Google Tag
   */
  personalisation: PropTypes.string,
  /**
   * The webchat Google Tag
   */
  webchat: PropTypes.string
};

export default CookieBanner;
